// UserQueriesReport.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { toast } from 'react-toastify';
import AdvancedFilter from './AdvancedFilter';
import ColumnChooser from './ColumnChooser';
import api, { getUserQueriesReport, saveColumnConfig } from '../helper/Api/AdminApi';

const DEFAULT_QUERY_COLUMNS = [
  { id: 'EMailID', label: 'Email ID', visible: true, sortable: true },
  { id: 'Objective', label: 'Objective', visible: true, sortable: true },
  { id: 'Message', label: 'Message', visible: true, sortable: true },
  { id: 'CreatedOn', label: 'Posted On', visible: true, sortable: true },
  { id: 'IsPreLogin', label: 'Query Type', visible: true, sortable: true }
];

// Added 100 to page size options
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];

const hasAdvancedFilterValue = (value) => {
  if (Array.isArray(value)) {
    return value.every((item) => String(item || '').trim() !== '');
  }
  return String(value || '').trim() !== '';
};

const buildDownloadBase64 = (base64Data, contentType) => {
  let cleanBase64 = (base64Data || '').trim();
  if (cleanBase64.includes('base64,')) {
    cleanBase64 = cleanBase64.split('base64,')[1];
  }
  cleanBase64 = cleanBase64.replace(/\s/g, '');

  while (cleanBase64.length % 4 !== 0) {
    cleanBase64 += '=';
  }

  const binaryString = window.atob(cleanBase64);
  const bytes = new Uint8Array(binaryString.length);

  for (let index = 0; index < binaryString.length; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }

  return new Blob([bytes], { type: contentType });
};

const mapConfigToColumns = (config = []) => {
  if (!config || !config.length) {
    return DEFAULT_QUERY_COLUMNS.map((column, index) => ({
      ...column,
      position: index + 1
    }));
  }

  const configMap = new Map(config.map((item) => [item.ColumnName, item]));

  const merged = DEFAULT_QUERY_COLUMNS.map((column, index) => {
    const matched = configMap.get(column.id);
    if (matched) {
      // Handle both Config (has DisplayName) and UserConfig (no DisplayName)
      const displayName = matched.DisplayName || column.label;
      const isVisible = matched.IsVisible === 1 || matched.IsVisible === true;
      const position = matched.Priority || matched.position || index + 1;

      return {
        ...column,
        label: displayName,
        visible: isVisible,
        position: position
      };
    }
    return {
      ...column,
      position: index + 1
    };
  });

  return merged.sort((a, b) => (a.position || 999) - (b.position || 999));
};

const UserQueriesReport = () => {
  // State for columns - only show visible columns
  const [allColumns, setAllColumns] = useState(DEFAULT_QUERY_COLUMNS);

  // Only show visible columns in the table
  const visibleColumns = allColumns.filter(col => col.visible);

  const [draggedCol, setDraggedCol] = useState(null);
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [queryType, setQueryType] = useState('3');
  const [showImages, setShowImages] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Sorting
  const [sortColumn, setSortColumn] = useState('CreatedOn');
  const [sortOrder, setSortOrder] = useState('desc');

  // Advanced filters
  const [advanceFilters, setAdvanceFilters] = useState([]);

  // Download states
  const [excelLoading, setExcelLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Ref to track if filters have been applied
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  // Store applied filters in ref to avoid re-renders
  const appliedFiltersRef = useRef({
    fromDate: '',
    toDate: '',
    queryType: '3',
    advanceFilters: [],
    sortColumn: 'CreatedOn',
    sortOrder: 'desc',
    pageSize: 10,
    currentPage: 1
  });

  // Prevent multiple API calls
  const isFirstRender = useRef(true);
  const isFetching = useRef(false);

  const baseURL = "https://api.amwaly.ma/uploads";

  // Filter fields for advanced filter
  const filterFields = [
    { value: 'EMailID', label: 'Email ID' },
    { value: 'Objective', label: 'Objective' },
    { value: 'Message', label: 'Message' }
  ];

  const appliedFilterItems = [
    queryType !== '3' && `Type: ${queryType === '2' ? 'Before Login' : 'After Login'}`,
    ...advanceFilters.map((filter) => {
      const label = filterFields.find((item) => item.value === filter.field)?.label || filter.field;
      const value = Array.isArray(filter.value) ? filter.value.filter(Boolean).join(' - ') : filter.value;
      return label && value ? `${label}: ${value}` : null;
    })
  ].filter(Boolean);

  // Calculate start and end entries for display
  const startEntry = queries.length ? (currentPage - 1) * pageSize + 1 : 0;
  const endEntry = Math.min(currentPage * pageSize, totalRecords);

  const syncColumnsFromResponse = useCallback((response) => {
    // Debug logs to check what's coming from API
    console.log('Response received:', response);
    console.log('UserQueryConfig:', response?.UserQueryConfig);
    console.log('QueryColumnConfig:', response?.QueryColumnConfig);

    // First check if UserQueryConfig exists and has items
    let configSource = null;

    if (response?.UserQueryConfig && Array.isArray(response.UserQueryConfig) && response.UserQueryConfig.length > 0) {
      console.log('Using UserQueryConfig as primary source');
      configSource = response.UserQueryConfig;
    } else if (response?.QueryColumnConfig && Array.isArray(response.QueryColumnConfig) && response.QueryColumnConfig.length > 0) {
      console.log('Using QueryColumnConfig as fallback source');
      configSource = response.QueryColumnConfig;
    }

    if (configSource && configSource.length > 0) {
      console.log('Config source being used:', configSource);
      const mappedColumns = mapConfigToColumns(configSource);
      console.log('Mapped columns:', mappedColumns);
      setAllColumns(mappedColumns);
    } else {
      console.log('No config found, using DEFAULT_QUERY_COLUMNS');
      // Keep default columns if no config found
    }
  }, []);

  // Fetch queries - uses ref for filters
  const fetchQueries = useCallback(async (page) => {
    // Prevent concurrent fetches
    if (isFetching.current) return;
    isFetching.current = true;

    setLoading(true);

    try {
      const filters = appliedFiltersRef.current;

      const params = {
        FromDate: filters.fromDate || '',
        ToDate: filters.toDate || '',
        IsPreLogin: filters.queryType === '3' ? '' : filters.queryType,
        SortingColumn: filters.sortColumn || 'CreatedOn',
        SortOrder: filters.sortOrder || 'desc',
        Page: page || filters.currentPage || 1,
        Limit: filters.pageSize || 10
      };

      // Only add advanced filters if they exist
      if (filters.advanceFilters && filters.advanceFilters.length > 0) {
        params.AdvanceFilter = filters.advanceFilters.map(filter => ({
          ColumnName: filter.field,
          Operator: filter.operator,
          Value: filter.value
        }));
      }

      const response = await getUserQueriesReport(params);

      setQueries(response?.Data || []);
      setTotalRecords(response?.TotalCount || 0);
      setTotalPages(response?.pagination?.total_pages || 0);

      // Update column visibility from response using the new sync function
      syncColumnsFromResponse(response);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch queries");
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  }, [syncColumnsFromResponse]);

  // Initial load - sets default dates and fetches data
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      const defaultFromDate = startDate.toISOString().split('T')[0];
      const defaultToDate = endDate.toISOString().split('T')[0];

      setFromDate(defaultFromDate);
      setToDate(defaultToDate);

      // Set applied filters
      appliedFiltersRef.current = {
        fromDate: defaultFromDate,
        toDate: defaultToDate,
        queryType: '3',
        advanceFilters: [],
        sortColumn: 'CreatedOn',
        sortOrder: 'desc',
        pageSize: 10,
        currentPage: 1
      };

      setIsFilterApplied(true);

      // Fetch initial data
      fetchQueries(1);
    }
  }, [fetchQueries]);

  // Handle pagination changes
  const handlePageChange = (page) => {
    if (page === currentPage) return;
    setCurrentPage(page);
    appliedFiltersRef.current.currentPage = page;
    fetchQueries(page);
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    if (newSize === pageSize) return;
    setPageSize(newSize);
    setCurrentPage(1);
    appliedFiltersRef.current.pageSize = newSize;
    appliedFiltersRef.current.currentPage = 1;
    fetchQueries(1);
  };

  // --- Drag and Drop Handlers ---
  const handleDragStart = (e, id) => {
    setDraggedCol(id);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const target = e.currentTarget;
    if (target.tagName === 'TH') {
      target.style.backgroundColor = '#e3f2fd';
      target.style.borderBottom = '3px solid #1976d2';
    }
  };

  const handleDragLeave = (e) => {
    const target = e.currentTarget;
    if (target.tagName === 'TH') {
      target.style.backgroundColor = '';
      target.style.borderBottom = '';
    }
  };

  const handleDrop = async (e, targetId) => {
    e.preventDefault();

    const target = e.currentTarget;
    if (target.tagName === 'TH') {
      target.style.backgroundColor = '';
      target.style.borderBottom = '';
    }

    if (!draggedCol || draggedCol === targetId) return;

    const nextColumns = [...allColumns];
    const draggedIndex = nextColumns.findIndex(col => col.id === draggedCol);
    const targetIndex = nextColumns.findIndex(col => col.id === targetId);

    const [draggedItem] = nextColumns.splice(draggedIndex, 1);
    nextColumns.splice(targetIndex, 0, draggedItem);

    const updatedColumns = nextColumns.map((col, index) => ({
      ...col,
      position: index
    }));

    setAllColumns(updatedColumns);
    setDraggedCol(null);

    try {
      await handleSaveColumnConfig(updatedColumns);
    } catch (error) {
      toast.error("Failed to save column configuration");
    }
  };

  // --- Column Configuration ---
  const handleColumnChange = (updatedColumns) => {
    setAllColumns(updatedColumns);
    handleSaveColumnConfig(updatedColumns);
  };

  const handleResetColumns = () => {
    const defaultColumns = DEFAULT_QUERY_COLUMNS.map((column, index) => ({
      ...column,
      position: index + 1
    }));
    setAllColumns(defaultColumns);
    handleSaveColumnConfig(defaultColumns);
    toast.info("Columns reset to default");
  };

  const handleSaveColumnConfig = async (cols) => {
    try {
      const saveData = cols.map((col, index) => ({
        ColumnName: col.id,
        Priority: index + 1,
        IsVisible: col.visible !== undefined ? col.visible : true
      }));

      await saveColumnConfig(saveData);
    } catch (error) {
      console.error("Failed to save column config:", error);
    }
  };

  // --- Sorting ---
  const handleSort = (column) => {
    if (!column.sortable) return;

    const newSortColumn = column.id;
    const newSortOrder = sortColumn === newSortColumn ? (sortOrder === 'desc' ? 'asc' : 'desc') : 'desc';

    setSortColumn(newSortColumn);
    setSortOrder(newSortOrder);

    // Update applied filters
    appliedFiltersRef.current.sortColumn = newSortColumn;
    appliedFiltersRef.current.sortOrder = newSortOrder;
    appliedFiltersRef.current.currentPage = 1;

    setCurrentPage(1);

    // Refetch with new sorting if filters are applied
    if (isFilterApplied) {
      fetchQueries(1);
    }
  };

  const handleViewClick = (advancedFiltersOverride) => {
    // If called as an event handler (e.g. onClick), the first arg will be
    // the click event rather than the filters array. Normalize to always
    // use an array of filters.
    const filtersToApply = Array.isArray(advancedFiltersOverride)
      ? advancedFiltersOverride
      : advanceFilters;

    const validationError = validateFilters(filtersToApply);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    syncAppliedFiltersFromCurrentState(filtersToApply, 1);

    setIsFilterApplied(true);
    setCurrentPage(1);

    // Fetch with new filters
    fetchQueries(1);
  };

  const validateFilters = useCallback((advancedFiltersOverride = advanceFilters) => {
    if (!fromDate || !toDate) {
      return "Please select both From Date and To Date";
    }

    if (new Date(fromDate) > new Date(toDate)) {
      return "From Date cannot be after To Date";
    }

    const invalidAdvanceRule = (advancedFiltersOverride || []).find((filter) => (
      filter.field && !hasAdvancedFilterValue(filter.value)
    ));

    if (invalidAdvanceRule) {
      return "Please enter a value for all selected advanced filter fields";
    }

    return "";
  }, [advanceFilters, fromDate, toDate]);

  const syncAppliedFiltersFromCurrentState = useCallback((advancedFiltersOverride = advanceFilters, page = currentPage) => {
    appliedFiltersRef.current = {
      fromDate: fromDate ? new Date(fromDate).toISOString().split('T')[0] : '',
      toDate: toDate ? new Date(toDate).toISOString().split('T')[0] : '',
      queryType,
      advanceFilters: advancedFiltersOverride,
      sortColumn,
      sortOrder,
      pageSize,
      currentPage: page
    };
  }, [advanceFilters, currentPage, fromDate, pageSize, queryType, sortColumn, sortOrder, toDate]);

  // --- Render Cell Content ---
  const renderCellContent = (query, column) => {
    const value = query[column.id];

    if (value === undefined || value === null) return '-';

    if (column.id === 'CreatedOn') {
      return new Date(value).toLocaleString();
    }

    if (column.id === 'IsPreLogin') {
      return value === '1' ? 'Before Login' :
        value === '0' ? 'After Login' : 'Unknown';
    }

    if (column.id === 'Objective' || column.id === 'Message') {
      return <ExpandableText text={value} />;
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return value;
  };

  // --- Expandable Text Component ---
  const ExpandableText = ({ text, maxLength = 50 }) => {
    const [expanded, setExpanded] = useState(false);

    if (!text || text.length <= maxLength) {
      return <>{text}</>;
    }

    return (
      <>
        {expanded ? text : `${text.substring(0, maxLength)}...`}
        <button
          type="button"
          className="btn btn-link text-primary-600 text-sm p-0 mt-1 text-decoration-none d-block"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Read Less' : 'Read More'}
        </button>
      </>
    );
  };

  // --- Export Handlers ---
  const handleExportExcel = async () => {
    const validationError = validateFilters();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setExcelLoading(true);

    try {
      syncAppliedFiltersFromCurrentState();
      setIsFilterApplied(true);
      const filters = appliedFiltersRef.current;

      const payload = {
        FromDate: filters.fromDate || '',
        ToDate: filters.toDate || '',
        IsPreLogin: filters.queryType === '3' ? '' : filters.queryType,
        OutPutType: 'excel',
        SortingColumn: filters.sortColumn || 'CreatedOn',
        SortOrder: filters.sortOrder || 'desc',
        Page: filters.currentPage || 1,
        Limit: filters.pageSize || 10
      };

      if (filters.advanceFilters && filters.advanceFilters.length > 0) {
        payload.AdvanceFilter = filters.advanceFilters.map(filter => ({
          ColumnName: filter.field,
          Operator: filter.operator,
          Value: filter.value
        }));
      }

      const response = await api.post("/admin/AdminGetUserQueries", payload, {
        responseType: 'json'
      });

      if (response.data?.Base64File) {
        const blob = buildDownloadBase64(
          response.data.Base64File,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        );

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const filename = response.data.FileName;
        link.download = filename;

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("Excel downloaded successfully!");
      } else {
        toast.error("No Excel data received from server");
      }

    } catch (error) {
      console.error('Excel Download Error:', error);
      toast.error(error.response?.data?.message || "Failed to download Excel");
    } finally {
      setExcelLoading(false);
    }
  };

  const handleExportPdf = async () => {
    const validationError = validateFilters();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setPdfLoading(true);

    try {
      syncAppliedFiltersFromCurrentState();
      setIsFilterApplied(true);
      const filters = appliedFiltersRef.current;

      const payload = {
        FromDate: filters.fromDate || '',
        ToDate: filters.toDate || '',
        IsPreLogin: filters.queryType === '3' ? '' : filters.queryType,
        OutPutType: 'pdf',
        SortingColumn: filters.sortColumn || 'CreatedOn',
        SortOrder: filters.sortOrder || 'desc',
        Page: filters.currentPage || 1,
        Limit: filters.pageSize || 10
      };

      if (filters.advanceFilters && filters.advanceFilters.length > 0) {
        payload.AdvanceFilter = filters.advanceFilters.map(filter => ({
          ColumnName: filter.field,
          Operator: filter.operator,
          Value: filter.value
        }));
      }

      const response = await api.post("/admin/AdminGetUserQueries", payload, {
        responseType: 'json'
      });

      if (response.data?.Base64File) {
        const blob = buildDownloadBase64(response.data.Base64File, 'application/pdf');

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const filename = response.data.FileName;
        link.download = filename;

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("PDF downloaded successfully!");
      } else {
        toast.error("No PDF data received from server");
      }

    } catch (error) {
      console.error('PDF Download Error:', error);
      toast.error(error.response?.data?.message || "Failed to download PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12 report-compact-card">
      {/* Header */}
      <div className="card-header border-bottom bg-base py-12 px-16">
        <div className="row g-3 align-items-end">
          {/* Date Filters */}
          <div className="col-lg-3 col-md-5">
            <div className="row g-2">
              <div className="col-md-6">
                <label className="form-label text-sm mb-1">From Date</label>
                <input
                  type="date"
                  className="form-control radius-8 h-40-px"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label text-sm mb-1">To Date</label>
                <input
                  type="date"
                  className="form-control radius-8 h-40-px"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="col-lg-9">
            <div className="d-flex gap-2 flex-wrap align-items-center" style={{ height: '40px' }}>
              {/* Show dropdown - moved to the leftmost position */}
              <div className="d-flex align-items-center gap-2">
                <span className="text-secondary-light text-sm fw-medium">Show:</span>
                <div className="dropdown">
                  <button
                    className="btn btn-outline-secondary btn-sm h-40-px px-12 radius-6 d-flex align-items-center gap-2"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    disabled={loading}
                    style={{
                      minWidth: '65px',
                      justifyContent: 'space-between',
                      backgroundColor: '#fff',
                      borderColor: '#d1d5db',
                      fontWeight: '500',
                      fontSize: '13px'
                    }}
                  >
                    <span>{pageSize}</span>
                    <Icon icon="ion:chevron-down-outline" className="text-secondary-light" style={{ fontSize: '14px' }} />
                  </button>
                  <ul className="dropdown-menu shadow-sm border-0 radius-8 py-2" style={{ minWidth: '65px' }}>
                    {PAGE_SIZE_OPTIONS.map((option) => (
                      <li key={option}>
                        <button
                          className={`dropdown-item py-2 px-16 text-sm ${pageSize === option ? 'active bg-primary-50 text-primary-600' : ''}`}
                          onClick={() => handlePageSizeChange(option)}
                          disabled={loading}
                          style={{
                            fontWeight: pageSize === option ? '600' : '400',
                            fontSize: '13px',
                            borderRadius: '6px'
                          }}
                        >
                          {option}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <button
                className="btn btn-primary btn-sm h-40-px d-inline-flex align-items-center gap-2"
                onClick={handleViewClick}
                disabled={loading}
              >
                <Icon icon="ion:eye-outline" className="icon" />
                View
              </button>

              <AdvancedFilter
                fields={filterFields}
                onApply={(filters) => {
                  setAdvanceFilters(filters);
                  handleViewClick(filters);
                }}
                onClear={() => {
                  setAdvanceFilters([]);
                }}
                filters={advanceFilters}
                triggerLabel="Advance Filters"
                onValidate={() => validateFilters()}
                hasFilterValues={!!fromDate || !!toDate || queryType !== '3'}
              >
                <div className="col-lg-10 col-md-10">
                  <label className="form-label text-sm mb-1 d-block">View Queries</label>
                  <div className="d-flex align-items-center gap-2 flex-nowrap" style={{ height: '40px' }}>
                    <div className="form-check d-flex align-items-center mb-0">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="queryType"
                        id="beforeLogin"
                        value="2"
                        checked={queryType === '2'}
                        onChange={(e) => setQueryType(e.target.value)}
                      />
                      <label className="form-check-label text-sm ms-1" htmlFor="beforeLogin">
                        Before Login
                      </label>
                    </div>
                    <div className="form-check d-flex align-items-center mb-0">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="queryType"
                        id="afterLogin"
                        value="1"
                        checked={queryType === '1'}
                        onChange={(e) => setQueryType(e.target.value)}
                      />
                      <label className="form-check-label text-sm ms-1" htmlFor="afterLogin">
                        After Login
                      </label>
                    </div>
                    <div className="form-check d-flex align-items-center mb-0">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="queryType"
                        id="allQueries"
                        value="3"
                        checked={queryType === '3'}
                        onChange={(e) => setQueryType(e.target.value)}
                      />
                      <label className="form-check-label text-sm ms-1" htmlFor="allQueries">
                        All
                      </label>
                    </div>
                  </div>
                </div>
              </AdvancedFilter>

              <ColumnChooser
                columns={allColumns}
                onChange={handleColumnChange}
                onReset={handleResetColumns}
              />

              <button
                className="btn btn-success btn-sm h-40-px d-inline-flex align-items-center gap-2"
                onClick={handleExportExcel}
                disabled={excelLoading}
              >
                {excelLoading ? (
                  <div className="spinner-border spinner-border-sm" />
                ) : (
                  <Icon icon="ri:file-excel-2-fill" className="icon" />
                )}
                Excel
              </button>

              <button
                className="btn btn-danger btn-sm h-40-px d-inline-flex align-items-center gap-2"
                onClick={handleExportPdf}
                disabled={pdfLoading}
              >
                {pdfLoading ? (
                  <div className="spinner-border spinner-border-sm" />
                ) : (
                  <Icon icon="ri:file-pdf-2-fill" className="icon" />
                )}
                PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card-body p-16">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" />
            <p className="mt-2">Loading...</p>
          </div>
        ) : (
          <>
            {appliedFilterItems.length > 0 && (
              <div className="compact-filter-summary mb-12">
                {appliedFilterItems.map((item) => (
                  <span key={item} className="compact-filter-chip">{item}</span>
                ))}
              </div>
            )}
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    {/* Image column - always visible */}
                    <th
                      key="action"
                      className="cursor-default align-middle"
                      style={{ minWidth: '80px', userSelect: 'none' }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-medium">Images</span>
                      </div>
                    </th>

                    {visibleColumns.map((column) => (
                      <th
                        key={column.id}
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, column.id)}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, column.id)}
                        className={`
                          cursor-grab
                          ${column.sortable ? 'sortable' : ''}
                          position-relative align-middle
                        `}
                        style={{
                          userSelect: 'none'
                        }}
                        onClick={() => column.sortable && handleSort(column)}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <Icon
                            icon="ion:menu-outline"
                            className="text-muted drag-handle"
                            style={{ fontSize: '14px', opacity: 0.5 }}
                          />
                          <span className="fw-medium">
                            {column.label}
                          </span>
                          {column.sortable && sortColumn === column.id && (
                            <Icon
                              icon={sortOrder === 'desc' ? 'ion:arrow-down-outline' : 'ion:arrow-up-outline'}
                              className="text-sm"
                            />
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {queries.length > 0 ? (
                    queries.map((query) => (
                      <tr key={query.ID}>
                        {/* Image column cell */}
                        <td className="align-middle">
                          <button
                            type="button"
                            className={`btn ${query.Images && query.Images.length > 0
                              ? 'btn-outline-primary'
                              : 'btn-outline-secondary'} 
                              btn-sm rounded-circle p-0`}
                            style={{ width: '32px', height: '32px' }}
                            onClick={() => query.Images && query.Images.length > 0 && setShowImages(query)}
                            disabled={!query.Images || query.Images.length === 0}
                            title={query.Images && query.Images.length > 0 ? "View Images" : "No Images"}
                          >
                            <Icon icon="mdi:image-outline" className="icon" />
                          </button>
                        </td>

                        {visibleColumns.map((column) => (
                          <td
                            key={column.id}
                            className="align-middle"
                            style={{
                              maxWidth: column.id === 'Objective' || column.id === 'Message' ? '250px' : 'auto',
                              whiteSpace: column.id === 'Objective' || column.id === 'Message' ? 'pre-wrap' : 'normal',
                              wordBreak: column.id === 'Objective' || column.id === 'Message' ? 'break-word' : 'normal'
                            }}
                          >
                            {renderCellContent(query, column)}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={visibleColumns.length + 1}
                        className="text-center py-4"
                      >
                        {isFilterApplied ? 'No queries found matching the criteria' : 'Please click "View" to load data'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination with Show dropdown and entries count */}
            {isFilterApplied && totalRecords > 0 && (
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-16">
                <div className="d-flex align-items-center gap-3">
                  {/* Entries count */}
                  <span className="text-secondary-light text-sm">
                    Showing {startEntry} to {endEntry} of {totalRecords} entries
                  </span>
                </div>

                {totalPages > 1 && (
                  <ul className="pagination d-flex flex-wrap align-items-center gap-2 m-0 p-0">
                    <li className="page-item">
                      <button
                        className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{ opacity: currentPage === 1 ? 0.4 : 1 }}
                      >
                        <Icon icon="ep:d-arrow-left" className="text-lg" />
                      </button>
                    </li>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <li className="page-item" key={pageNum}>
                          <button
                            className={`page-link radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px fw-medium text-sm ${currentPage === pageNum
                              ? 'bg-primary-600 text-white shadow-sm'
                              : 'bg-neutral-100 text-secondary-light'
                              }`}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}

                    <li className="page-item">
                      <button
                        className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        style={{ opacity: currentPage === totalPages ? 0.4 : 1 }}
                      >
                        <Icon icon="ep:d-arrow-right" className="text-lg" />
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Images Modal */}
      {showImages && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border shadow-lg" style={{ borderRadius: '12px' }}>
              <div className="modal-header p-15 border-bottom">
                <h4 className="modal-title text-xl fw-semibold text-primary-light">
                  Attached Images ({showImages.Images?.length || 0})
                </h4>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowImages(null)}
                />
              </div>
              <div className="modal-body p-24">
                <div className="row">
                  {showImages.Images?.map((imageName, index) => (
                    <div className="col-md-6 mb-3" key={index}>
                      <div className="card border">
                        <div className="card-body p-3">
                          <p className="text-sm mb-2">{imageName}</p>
                          {showImages.SysImages?.[index] && (
                            <img
                              src={`${baseURL}/${showImages.SysImages[index]}`}
                              alt={imageName}
                              className="img-fluid rounded"
                              style={{ maxHeight: '200px', objectFit: 'contain' }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="d-flex justify-content-center mt-3">
                  <button
                    className="btn btn-primary px-56 py-12 radius-8"
                    onClick={() => setShowImages(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .compact-filter-summary {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .compact-filter-chip {
          display: inline-flex;
          align-items: center;
          min-height: 24px;
          padding: 2px 8px;
          border-radius: 999px;
          background: rgba(15, 23, 42, 0.05);
          border: 1px solid rgba(148, 163, 184, 0.3);
          font-size: 11px;
          line-height: 1.2;
          color: #334155;
        }

        .report-compact-card :global(th),
        .report-compact-card :global(td),
        .report-compact-card :global(.form-label),
        .report-compact-card :global(.form-check-label) {
          vertical-align: middle;
        }
      `}</style>
    </div>
  );
};

export default UserQueriesReport;